import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { downsample, timelineHasEnergy } from "../../utils/nativeReportUtils";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler, Legend);

function readSeries(point, keys) {
  for (const key of keys) {
    const value = Number(point?.[key]);
    if (Number.isFinite(value)) return value;
  }
  return 0;
}

function stretchSeries(values) {
  if (!values.length) return values;
  const low = Math.min(...values);
  const high = Math.max(...values);
  const span = high - low;
  if (span >= 0.25) return values;
  if (span < 1e-6) return values.map(() => 0.5);
  return values.map((value) => 0.08 + (0.84 * (value - low)) / span);
}

function ReportTimelineChart({ timeline, emphasizeShape = false }) {
  const points = useMemo(() => downsample(timeline || [], 80), [timeline]);
  const energy = useMemo(
    () => points.map((point) => readSeries(point, ["energy", "arousalNorm", "momentum"])),
    [points]
  );
  const friction = useMemo(
    () => points.map((point) => readSeries(point, ["friction", "riskRatio"])),
    [points]
  );
  const energyPlot = emphasizeShape ? stretchSeries(energy) : energy;
  const frictionPlot = emphasizeShape ? stretchSeries(friction) : friction;
  const hasEnergy = timelineHasEnergy(points);

  if (!points.length) return null;

  const sparse = points.length <= 8;
  const data = {
    labels: points.map((point) => {
      const stage = String(point.stage || "").trim();
      if (stage) return stage.charAt(0).toUpperCase() + stage.slice(1);
      return `${Math.round(Number(point.progressPct) || 0)}%`;
    }),
    datasets: [
      ...(hasEnergy
        ? [
            {
              label: "Energy",
              data: energyPlot,
              borderColor: "#2dd4bf",
              backgroundColor: "rgba(45, 212, 191, 0.16)",
              pointRadius: sparse ? 4 : 0,
              tension: 0.35,
              fill: true,
              yAxisID: "y",
            },
          ]
        : []),
      {
        label: "Friction",
        data: frictionPlot,
        borderColor: "#fb7185",
        backgroundColor: "transparent",
        pointRadius: sparse ? 4 : 0,
        tension: 0.35,
        fill: false,
        yAxisID: "y",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        display: true,
        labels: { color: "#9ca3af", boxWidth: 10, font: { size: 11 } },
      },
      tooltip: {
        backgroundColor: "#1c1b28",
        borderColor: "rgba(255,255,255,0.1)",
        borderWidth: 1,
        titleColor: "#f4f4f5",
        bodyColor: "#9ca3af",
        padding: 10,
      },
    },
    scales: {
      x: {
        grid: { color: "rgba(255,255,255,0.04)" },
        ticks: { color: "#6b7280", font: { size: 10 }, maxTicksLimit: 8 },
      },
      y: {
        beginAtZero: true,
        max: 1,
        grid: { color: "rgba(255,255,255,0.04)" },
        ticks: { color: "#6b7280", font: { size: 10 } },
      },
    },
  };

  return (
    <div className="dev-chart-wrap native-report-chart">
      <Line data={data} options={options} />
    </div>
  );
}

export default ReportTimelineChart;
